import React, { useState } from 'react';
import { useCrudOperations } from '../../../components/Hooks/useCrudOperations';
import { API_URL } from '../../../utils/api';
import { invalidateGlobalCache } from '../../../hooks/useApiCache';

function RemoveUser({ user, setUsers, users, buttonClassName, iconClassName, onSuccess }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState('');
  const { removeItem } = useCrudOperations(`${API_URL}/users`, setUsers, users);

  const handleRemoveClick = async (e) => {
    e.stopPropagation();
    setError('');
    setOpenDialog(true);
    setLoadingPreview(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/${user._id}/delete-preview`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPreview(data);
      }
    } catch (err) {
      console.warn('Kunde inte hämta preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleting(true);
      await removeItem(user._id);
      setDeleted(true);

      invalidateGlobalCache([
        'dashboard_users',
        'myassigned_users',
        'dashboard_projects',
        'myassigned_projects',
        'myprojects_projects'
      ]);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      setError('Kunde inte ta bort användaren. Försök igen senare.');
      console.error('Error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseDialog = (e) => {
    if (e) e.stopPropagation();
    setOpenDialog(false);
    setPreview(null);
    setDeleted(false);
    setError('');
  };

  const dataLabels = [
    ['leads', 'Leads'],
    ['procurements', 'Upphandlingar'],
    ['estimat', 'Estimat'],
    ['kalkyler', 'Kalkyler'],
    ['puThreads', 'PU-trådar'],
    ['offerter', 'Offerter'],
    ['offertThreads', 'Offertkommentarer'],
    ['projects', 'Projekt'],
    ['atas', 'ÄTA'],
    ['ataThreads', 'ÄTA-kommentarer'],
    ['timelines', 'Tidplaner'],
    ['kanbans', 'Kanban-tavlor'],
    ['briefs', 'Brief'],
    ['shoppingLists', 'Inköpslistor'],
    ['rumsas', 'Rumsbeskrivningar'],
    ['meetings', 'Möten'],
    ['meetingProtocols', 'Mötesprotokoll'],
    ['meetingNotes', 'Mötesanteckningar'],
    ['todos', 'Todos'],
    ['notifications', 'Notifikationer'],
    ['procurementUpdates', 'Upphandlingsuppdateringar'],
    ['projectUpdates', 'Projektuppdateringar'],
    ['userSettings', 'Inställningar'],
  ];

  return (
    <>
      <button
        onClick={handleRemoveClick}
        className={buttonClassName}
      >
        <svg
          className={iconClassName}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>

      {openDialog && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50"
          onClick={handleCloseDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Ta bort användare</h3>
            </div>

            <div className="px-5 py-4 space-y-4">
              {deleted ? (
                <div className="flex flex-col items-center py-4">
                  <svg className="w-12 h-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-sm font-medium text-gray-800">
                    {user.firstName} {user.lastName} och all relaterad data har tagits bort.
                  </p>
                </div>
              ) : (
              <>
              <p className="text-sm text-gray-700">
                Är du säker på att du vill ta bort <strong>{user.firstName} {user.lastName}</strong>?
              </p>

              {loadingPreview ? (
                <div className="flex items-center justify-center py-4">
                  <svg className="w-5 h-5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="ml-2 text-sm text-gray-500">Hämtar data...</span>
                </div>
              ) : preview?.summary && (
                <>
                  {dataLabels.some(([key]) => preview.summary[key] > 0) ? (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Följande data tas bort:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {dataLabels
                          .filter(([key]) => preview.summary[key] > 0)
                          .map(([key, label]) => (
                            <div key={key} className="flex justify-between text-sm bg-red-50 rounded px-3 py-1.5">
                              <span className="text-gray-700">{label}</span>
                              <span className="font-medium text-red-700">{preview.summary[key]}</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Ingen relaterad data hittades.</p>
                  )}

                  {preview?.willMoveSharepointFolder ? (
                    <p className="text-sm text-red-600 font-medium">
                      SharePoint-mappen flyttas till "Nej tack". Denna åtgärd kan inte ångras.
                    </p>
                  ) : (
                    <p className="text-sm text-blue-700 font-medium">
                      Användaren tas bort som beställare 2 från upphandlingen. SharePoint-mappen rörs inte (den tillhör huvudkunden). Denna åtgärd kan inte ångras.
                    </p>
                  )}
                </>
              )}

              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              </>
              )}
            </div>

            <div className="px-5 py-4 bg-gray-50 border-t flex justify-end gap-2">
              {deleted ? (
                <button
                  onClick={handleCloseDialog}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Stäng
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCloseDialog}
                    disabled={deleting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleting || loadingPreview}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-300"
                  >
                    {deleting ? 'Tar bort...' : 'Ta bort'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RemoveUser;
